import { useCallback, useEffect, useRef, useState } from "react";
import { api, esNoAutorizado } from "./lib/api";
import { t } from "./lib/i18n";
import type { Fuente, Idioma, Origen, Salud, SesionResumen, Sugerencia } from "./lib/tipos";
import { useChat } from "./hooks/useChat";
import { BarraLateral } from "./componentes/BarraLateral";
import { Composer } from "./componentes/Composer";
import { Mensaje } from "./componentes/Mensaje";
import { Sugerencias } from "./componentes/Sugerencias";
import { Simbolo } from "./componentes/Logo";
import { Login } from "./componentes/Login";
import { useEsMovil } from "./lib/movil";

type Tema = "claro" | "oscuro" | "sistema";

function leer<T extends string>(clave: string, porDefecto: T): T {
  try {
    return (localStorage.getItem(clave) as T) || porDefecto;
  } catch {
    return porDefecto;
  }
}

export function App() {
  const [idioma, setIdioma] = useState<Idioma>(() => leer<Idioma>("tim.idioma", "en"));
  const [tema, setTema] = useState<Tema>(() => leer<Tema>("tim.tema", "sistema"));
  const [salud, setSalud] = useState<Salud | null>(null);
  const [fuentes, setFuentes] = useState<Fuente[]>([]);
  // La procedencia del mundo: unión, no mínimo. Una sola fuente real ya hace que «los datos son
  // sintéticos» sea falso, y mezclar es un estado propio — el mismo criterio que el motor
  // (`motor/base.origen_compuesto`), aquí solo para el descargo permanente.
  const origenMundo: Origen = (() => {
    const vistos = new Set(fuentes.map((f) => f.origen).filter(Boolean));
    if (vistos.size === 0 || (vistos.size === 1 && vistos.has("sintetico"))) return "sintetico";
    if (vistos.size === 1 && vistos.has("real")) return "real";
    return "mixto";
  })();
  const [sugerencias, setSugerencias] = useState<Sugerencia[]>([]);
  const [sesiones, setSesiones] = useState<SesionResumen[]>([]);
  const [borrador, setBorrador] = useState("");
  // `comprobando` mientras no se sabe: sin ese estado, la pantalla de acceso parpadea un
  // instante en cada carga aunque la cookie sea válida.
  const [acceso, setAcceso] = useState<"comprobando" | "fuera" | "dentro">("comprobando");
  // En un teléfono la barra lateral se comía 232 px de 812 —el 29 % de la pantalla— para navegar
  // entre conversaciones, que es lo que menos se hace. Pasa a cajón detrás de un botón.
  const movil = useEsMovil();
  const [lateralAbierta, setLateralAbierta] = useState(false);
  // PLEGAR LA BARRA EN ESCRITORIO. Son 272 px fijos que el chat no tiene, y delante de una tabla
  // de comparación ancha —varios artículos en columnas— se agradecen. En móvil no aplica: ahí la
  // barra ya es un cajón. Se recuerda, dentro de un try: sin almacenamiento la app arranca con la
  // barra visible, que es el estado seguro.
  const [lateralPlegada, setLateralPlegada] = useState(() => {
    try {
      return localStorage.getItem("tim.lateral") === "plegada";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("tim.lateral", lateralPlegada ? "plegada" : "abierta");
    } catch {
      /* sin almacenamiento: se pliega igual, solo que no se recuerda */
    }
  }, [lateralPlegada]);
  const botonMenuRef = useRef<HTMLButtonElement>(null);
  const lateralRef = useRef<HTMLDivElement>(null);
  const chat = useChat();
  const finRef = useRef<HTMLDivElement>(null);
  const enVuelo = chat.turnos.some((x) => x.enVuelo);

  useEffect(() => {
    document.documentElement.dataset.theme = tema === "sistema" ? "" : tema === "oscuro" ? "dark" : "light";
    if (tema === "sistema") delete document.documentElement.dataset.theme;
    try { localStorage.setItem("tim.tema", tema); } catch { /* modo privado */ }
  }, [tema]);

  useEffect(() => {
    document.documentElement.lang = idioma;
    try { localStorage.setItem("tim.idioma", idioma); } catch { /* modo privado */ }
  }, [idioma]);

  const refrescarSesiones = useCallback(() => {
    api.sesiones()
      .then((s) => { setSesiones(s); setAcceso("dentro"); })
      // Un 401 aquí no es «no hay sesiones»: es que la cookie caducó a mitad de uso. Distinguirlo
      // evita el peor caso, una barra lateral vacía que parece un bug de datos.
      .catch((e) => { setSesiones([]); if (esNoAutorizado(e)) setAcceso("fuera"); });
  }, []);

  const [usuario, setUsuario] = useState<string | null>(null);

  const cargar = useCallback(() => {
    // `/healthz` es la única ruta sin token, a propósito: sirve para el healthcheck de la
    // plataforma y para pintar el modo antes de entrar.
    api.salud().then(setSalud).catch(() => setSalud(null));
    // Con la API abierta no hay usuario que mostrar, y un 401 aquí solo dice que no has entrado.
    api.yo().then((d) => setUsuario(d.usuario)).catch(() => setUsuario(null));
    api.fuentes().then((d) => { setFuentes(d.fuentes); }).catch(() => setFuentes([]));
    api.sugerencias().then((d) => setSugerencias(d.sugerencias)).catch(() => setSugerencias([]));
    refrescarSesiones();
  }, [refrescarSesiones]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!enVuelo) refrescarSesiones();
  }, [enVuelo, refrescarSesiones]);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [chat.turnos.length, chat.turnos.at(-1)?.tarjetas.length]);

  // Al ensanchar la ventana el cajón deja de existir: si se queda "abierto" en el estado, la
  // barra lateral de escritorio hereda las clases del cajón y aparece flotando.
  useEffect(() => { if (!movil) setLateralAbierta(false); }, [movil]);

  // Cerrar SIEMPRE devuelve el foco al botón, por cualquier vía: Escape, el fondo o elegir una
  // conversación. Si no, el foco se queda dentro de un cajón que acaba de volverse `inert` y el
  // siguiente tabulador no va a ninguna parte visible. Medido: al tocar el fondo el foco se
  // quedaba en «Nueva conversación», invisible y fuera del árbol accesible.
  const cerrarCajon = useCallback(() => {
    setLateralAbierta(false);
    botonMenuRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!lateralAbierta) return;
    const alTecla = (e: KeyboardEvent) => { if (e.key === "Escape") cerrarCajon(); };
    document.addEventListener("keydown", alTecla);
    lateralRef.current?.querySelector<HTMLElement>("button, a[href]")?.focus();
    return () => document.removeEventListener("keydown", alTecla);
  }, [lateralAbierta, cerrarCajon]);

  const txt = t(idioma);
  const preguntar = (texto: string) => void chat.preguntar(texto, idioma);

  // Elegir una conversación en el cajón lo cierra: si no, tapa la respuesta que se acaba de abrir.
  const cerrarSiMovil = () => { if (movil && lateralAbierta) cerrarCajon(); };

  if (acceso === "comprobando") return <div className="acceso" aria-busy="true" />;
  if (acceso === "fuera") {
    return <Login idioma={idioma} alIdioma={setIdioma} alEntrar={() => { setAcceso("comprobando"); cargar(); }} />;
  }

  return (
    <div className={`app${movil && lateralAbierta ? " con-cajon" : ""}${!movil && lateralPlegada ? " sin-lateral" : ""}`}>
      <BarraLateral
        ref={lateralRef}
        enCajon={movil} abierta={lateralAbierta} plegada={!movil && lateralPlegada}
        idioma={idioma} salud={salud} fuentes={fuentes} sesiones={sesiones} sesionId={chat.sesionId} tema={tema}
        alNueva={() => { chat.nueva(); cerrarSiMovil(); }}
        alAbrir={(id) => { void chat.abrir(id); cerrarSiMovil(); }}
        alBorrar={(id) => void api.borrarSesion(id).then(() => { refrescarSesiones(); if (id === chat.sesionId) chat.nueva(); })}
        alIdioma={setIdioma} alTema={setTema}
        usuario={usuario}
        alSalir={() => void api.logout().finally(() => { chat.nueva(); setUsuario(null); setAcceso("fuera"); })}
      />
      {movil && lateralAbierta && (
        // El fondo cierra al tocar. `aria-hidden` porque Escape y el botón ya cubren el teclado.
        <div className="cajon-fondo" onClick={cerrarCajon} aria-hidden="true" />
      )}
      <main className="principal">
        <header className="barra-superior">
          {/* El MISMO botón en los dos sitios: en móvil abre el cajón, en escritorio pliega la
              barra. Dos controles distintos para «enseñar u ocultar la barra» habrían sido dos
              cosas que aprender para una sola idea. */}
          <button ref={botonMenuRef} className="menu"
                  onClick={() => (movil ? setLateralAbierta((v) => !v) : setLateralPlegada((v) => !v))}
                  aria-label={(movil ? lateralAbierta : !lateralPlegada) ? txt.cerrarMenu : txt.abrirMenu}
                  aria-expanded={movil ? lateralAbierta : !lateralPlegada} aria-controls="lateral">
            {movil ? (lateralAbierta ? "✕" : "☰") : lateralPlegada ? "☰" : "⟨"}
          </button>
          <span className="agente-marca">
            <Simbolo tamano={24} titulo="Essity" />
            TIM <em>{txt.agente}</em>
          </span>
          <span className="barra-contexto">{txt.subtitulo}</span>
          {salud && <span className="barra-modo" title={`${salud.modo} · ${salud.acceso}`}>{salud.modo}</span>}
        </header>
        <div className="hilo">
          {chat.turnos.length === 0 && (
            <div className="bienvenida">
              <span className="bienvenida-icono"><Simbolo tamano={38} titulo="Essity" /></span>
              <h2>{txt.comoAyudo}</h2>
              <p>{txt.intro}</p>
              <Sugerencias sugerencias={sugerencias} idioma={idioma} alElegir={preguntar} />
            </div>
          )}
          {chat.turnos.map((turno) => (
            <Mensaje key={turno.id} turno={turno} idioma={idioma} sesionId={chat.sesionId} />
          ))}
          {chat.error && <p className="mal">{txt.error}: {chat.error}</p>}
          <div ref={finRef} />
        </div>
        <Composer idioma={idioma} ocupado={enVuelo} valor={borrador} alValor={setBorrador} origenMundo={origenMundo}
                  alEnviar={preguntar} alDetener={chat.detener} />
      </main>
    </div>
  );
}
